import React, { useState } from 'react';
import { Info } from 'lucide-react';
import { useBoardAnalytics, PeriodKey } from '../../../hooks/useBoardAnalytics';
import AnalyticsDemoOverlay from './analytics/AnalyticsDemoOverlay';
import FunnelChart from './analytics/FunnelChart';
import KpiCards from './analytics/KpiCards';
import JobsOverTimeChart from './analytics/JobsOverTimeChart';
import ActivitiesByTypeChart from './analytics/ActivitiesByTypeChart';
import StaleJobsList from './analytics/StaleJobsList';
import UpcomingActivitiesList from './analytics/UpcomingActivitiesList';
import '../../../styles/kanbans/analytics.css';

interface MetricsTabProps {
  boardId?: string;
}

function timeAgo(iso: string): string {
  try {
    const diffMs = Date.now() - new Date(iso).getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin} min ago`;
    const diffH = Math.floor(diffMin / 60);
    return `${diffH}h ago`;
  } catch {
    return '';
  }
}

export default function MetricsTab({ boardId }: MetricsTabProps) {
  const { data, isLoading, isDemo, error } = useBoardAnalytics(boardId);
  const [showValues, setShowValues] = useState(true);

  if (isLoading) {
    return <div className="metrics-empty">Loading analytics…</div>;
  }

  if (error) {
    return <div className="metrics-empty">Failed to load analytics.</div>;
  }

  if (!isDemo && !data) {
    return (
      <div className="metrics-empty">
        Analytics are being computed. Check back in a few minutes.
      </div>
    );
  }

  return (
    <AnalyticsDemoOverlay isDemo={isDemo}>
      <div className="metrics-tab">

        <div className="metrics-toolbar">
          {data?.computed_at ? (
            <div className="metrics-last-calculated">
              <span className="metrics-last-calculated__icon" title={`Computed at: ${new Date(data.computed_at).toLocaleString()}`}>
                <Info size={13} />
              </span>
              Last calculated: {timeAgo(data.computed_at)}
            </div>
          ) : <span />}
          <label className="metrics-toggle">
            <input
              type="checkbox"
              checked={showValues}
              onChange={(e) => setShowValues(e.target.checked)}
            />
            Show values
          </label>
        </div>

        {data && <FunnelChart periods={data.periods} showValues={showValues} />}

        {data && (
          <div className="metrics-card">
            <div className="metrics-card__header">
              <span className="metrics-card__title">Key Metrics</span>
            </div>
            <KpiCards kpis={data.periods['30d'].kpis} />
          </div>
        )}

        {data && (
          <div className="metrics-grid-2">
            <JobsOverTimeChart periods={data.periods} showValues={showValues} />
            <ActivitiesByTypeChart periods={data.periods} showValues={showValues} />
          </div>
        )}

        {data && (
          <div className="metrics-grid-2">
            <StaleJobsList staleJobs={data.stale_jobs} boardId={boardId ?? ''} />
            <UpcomingActivitiesList activities={data.upcoming_activities} />
          </div>
        )}

      </div>
    </AnalyticsDemoOverlay>
  );
}
